import isEqual from 'lodash/isEqual'
import React, {
  useCallback,
  MutableRefObject,
  useRef,
  useState,
  useEffect
} from 'react'
import { InputGroup, InputField } from '../Controls'
import { UseFormReturn, FieldError } from 'react-hook-form'
import { CheckoutForm, FormFields } from './types'
import {
  focusNextInput,
  onlySpaces,
  getAddressErrorMessage,
  hasAddressErrors,
  SHIPPING_INPUTS,
  SHIPPING_PREFIX,
  isAddressParamsValid
} from './utils'
import AlertMessage from '../CartModal/AlertMessage'
import styled from '@emotion/styled'
import { inputCharactersLimitValidator } from 'shop/components/Checkout'
import useInputKeyEnterHandler from 'shop/hooks/useInputKeyEnterHandler'
import FormInput from '../Inputs/FormInput'
import { HalfWidthFlexContainer } from './commonStyles'
import { DomesticAddress } from 'shop/types/cart'
import { useCheckout, useConsumerCart } from 'shop/hooks'
import { handleFormFieldCartUpdate } from './formCartUpdates'
import { useFocusOutListener } from 'shop/hooks/useFocusOutListener'

type Props = {
  formHandle: UseFormReturn<FormFields>
  deliveryNotePlaceholder?: string
  isDeliveryLoading: boolean
  setIsDeliveryLoading: (value: boolean) => void
  updateCart: (address: DomesticAddress) => void
  onBlur: () => void
  displayedAddress: DomesticAddress
  prevAddress: MutableRefObject<DomesticAddress | undefined>
  isDeliveryAddressFormOpen?: boolean
  setIsDeliveryAddressFormOpen: React.Dispatch<React.SetStateAction<boolean>>
  prevDropoffNotesRef: React.MutableRefObject<
    CheckoutForm['dropoffNotes'] | undefined
  >
}

const ShippingDetails = ({
  formHandle,
  deliveryNotePlaceholder = '',
  isDeliveryLoading,
  setIsDeliveryLoading,
  updateCart,
  onBlur,
  displayedAddress,
  prevAddress,
  isDeliveryAddressFormOpen,
  setIsDeliveryAddressFormOpen,
  prevDropoffNotesRef
}: Props) => {
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const {
    register,
    formState: { errors },
    watch
  } = formHandle
  const deliveryAddress = watch('deliveryAddress')
  const dropoffNotes = watch('dropoffNotes')
  const { isTouched } = formHandle.getFieldState('deliveryAddress')

  const { trackAddShippingInfo } = useCheckout()
  const { updateConsumerCart } = useConsumerCart()

  const [attemptFormClose, setAttemptFormClose] = useState(false)
  const [startShowingErrors, setStartShowingErrors] = useState(false)

  const addressErrors: { [key: string]: FieldError } | undefined = errors[
    'deliveryAddress'
  ] as { [key: string]: FieldError }
  const addressApiError: FieldError | undefined = errors[
    'address_api'
  ] as FieldError

  const handleQuoting = useCallback(
    (address: DomesticAddress) => {
      if (
        !displayedAddress ||
        hasAddressErrors(addressErrors) ||
        isEqual(address, prevAddress.current)
      ) {
        return
      }

      prevAddress.current = { ...address }
      const addressValid = isAddressParamsValid(address)
      if (!addressValid) {
        return
      }

      setIsDeliveryLoading(true)

      trackAddShippingInfo()

      updateCart(address)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addressErrors, setIsDeliveryLoading, updateCart]
  )

  const handleBlur = () => {
    handleQuoting(formHandle.getValues('deliveryAddress'))
    onBlur()
  }

  const {
    line1InputId,
    line2InputId,
    zipInputId,
    cityInputId,
    dropoffNotesInputId,
    saveAddressInputId
  } = SHIPPING_INPUTS
  const inputList = [
    line1InputId,
    line2InputId,
    zipInputId,
    cityInputId,
    dropoffNotesInputId,
    saveAddressInputId
  ]

  useInputKeyEnterHandler({ handleKeyPress: () => focusNextInput(inputList) })

  const currentError = ((): string | null => {
    if (hasAddressErrors(addressErrors)) {
      return getAddressErrorMessage(addressErrors)
    }

    if (Boolean(addressApiError)) {
      return addressApiError.message || ''
    }

    return null
  })()

  // Set listener for when the form section loses focus to trigger update cart and collapse.
  const handleFocusOut = async (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null

    // If blur happens and focus doesn't move to another element inside the form section
    const isLeavingSection =
      !relatedTarget || !sectionRef.current?.contains(relatedTarget)
    if (isLeavingSection) {
      handleQuoting(formHandle.getValues('deliveryAddress'))
      handleFormFieldCartUpdate({
        formRef: sectionRef,
        checkoutForm: formHandle,
        event: e,
        prevFieldValue: prevDropoffNotesRef,
        formFieldName: 'dropoffNotes',
        updateConsumerCart
      })
      setAttemptFormClose(true)
    }
  }
  useFocusOutListener({ sectionRef, onFocusOut: handleFocusOut })

  // Collapse the form section if triggered and valid to do so
  useEffect(() => {
    const addressValid = isAddressParamsValid(
      formHandle.getValues('deliveryAddress')
    )
    const canClose =
      !isDeliveryLoading &&
      !addressApiError &&
      !hasAddressErrors(addressErrors) &&
      addressValid

    if (canClose && attemptFormClose) {
      setIsDeliveryAddressFormOpen(false)
      setAttemptFormClose(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeliveryLoading, attemptFormClose, addressApiError, addressErrors])

  // Don't show error on load if delivery address fields are invalid and the user is yet to interact with it
  useEffect(() => {
    if (startShowingErrors) return

    const calcShouldShowErrors = async () => {
      const isValid = await formHandle.trigger('deliveryAddress')
      // clear the errors quickly after trigger so the form doesn't display any invalid states
      formHandle.clearErrors('deliveryAddress')

      // fields are valid, or user has interacted with the form, show all possible errors
      if (isValid || isTouched) setStartShowingErrors(true)
    }

    calcShouldShowErrors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouched])

  return (
    <Container>
      {currentError && startShowingErrors && (
        <AlertMessageWrapper>
          <AlertMessage skin='mild' type='error' heading={currentError} />
        </AlertMessageWrapper>
      )}

      {/*
        AutoComplete weirdness. To turn off properly in browser, must give an invalid value. ¯\_(ツ)_/¯
        Ref: https://stackoverflow.com/a/54041517
      */}
      <Fields ref={sectionRef}>
        <InputField
          {...register(SHIPPING_PREFIX).ref}
          hidden={true}
          name={SHIPPING_PREFIX}
          inputType='checkbox'
        />
        <InputGroup templateColumns={'auto'} gridGap='18px'>
          <FormInput
            id={line1InputId}
            name={line1InputId}
            labelText={'Street Address'}
            value={deliveryAddress?.lineOne || ''}
            formRef={register('deliveryAddress.lineOne', {
              validate: {
                isRequired: (value) =>
                  Boolean(value.length) && !onlySpaces(value)
              }
            })}
            showError={!!errors.deliveryAddress?.lineOne}
            autoComplete={'street-address'}
            onBlur={handleBlur}
            scrollSettings={{
              scrollOnFocus: true,
              scrollToElementId: 'fulfillment-section'
            }}
            tabIndex={!isDeliveryAddressFormOpen ? -1 : 0}
          />
          <FormInput
            id={line2InputId}
            name={line2InputId}
            labelText={'Apartment or building name (optional)'}
            value={deliveryAddress?.lineTwo || ''}
            formRef={register('deliveryAddress.lineTwo')}
            showError={!!errors.deliveryAddress?.lineTwo}
            autoComplete={'not-this-one'}
            onBlur={handleBlur}
            scrollSettings={{
              scrollOnFocus: true,
              scrollToElementId: 'fulfillment-section'
            }}
            tabIndex={!isDeliveryAddressFormOpen ? -1 : 0}
          />
          <HalfWidthFlexContainer>
            <FormInput
              id={zipInputId}
              name={zipInputId}
              labelText={'Postcode'}
              value={deliveryAddress?.zip || ''}
              formRef={register('deliveryAddress.zip', {
                validate: {
                  isRequired: (value) =>
                    Boolean(value.length) && !onlySpaces(value),
                  length_5: (value) => value.length > 4
                }
              })}
              showError={!!errors.deliveryAddress?.zip}
              autoComplete={'postal-code'}
              onBlur={handleBlur}
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'fulfillment-section'
              }}
              tabIndex={!isDeliveryAddressFormOpen ? -1 : 0}
            />
            <FormInput
              id={cityInputId}
              name={cityInputId}
              labelText={'City'}
              value={deliveryAddress?.city || ''}
              formRef={register('deliveryAddress.city', {
                validate: {
                  isRequired: (value) =>
                    Boolean(value.length) && !onlySpaces(value)
                }
              })}
              autoComplete={'address-level2'}
              onBlur={handleBlur}
              showError={!!errors.deliveryAddress?.city}
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'fulfillment-section'
              }}
              tabIndex={!isDeliveryAddressFormOpen ? -1 : 0}
            />
          </HalfWidthFlexContainer>
          <FormInput
            id={dropoffNotesInputId}
            name={dropoffNotesInputId}
            labelText='Delivery instructions'
            variant='textarea'
            placeholder={deliveryNotePlaceholder}
            value={dropoffNotes || ''}
            formRef={register('dropoffNotes', {
              validate: inputCharactersLimitValidator('Delivery instructions')
            })}
            showError={!!errors[dropoffNotesInputId]}
            scrollSettings={{
              scrollOnFocus: true,
              scrollToElementId: 'fulfillment-section'
            }}
            tabIndex={!isDeliveryAddressFormOpen ? -1 : 0}
          />
        </InputGroup>
      </Fields>
    </Container>
  )
}

const Fields = styled.div(() => ({
  display: 'block'
}))

const AlertMessageWrapper = styled.div(() => ({
  marginBottom: '16px'
}))

const Container = styled.div(() => ({
  paddingBottom: '16px'
}))

export default ShippingDetails
